import React, { useMemo } from "react";

import ErrorIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Fade, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { transitionsKeyMaker } from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useGetTransitions";
import { GetTransitionsQueryParams } from "@fartherfinance/frontend/api/TransitionWorkflows/requests/getTransitions";
import { TransitionDTO } from "@fartherfinance/frontend/api/TransitionWorkflows/Types";
import { PaginationMetadata } from "@fartherfinance/frontend/api/Types";
import { formatShortCash } from "@fartherfinance/frontend/formatting/numbers";

import TransitionStatusChip from "../TransitionStatusChip/TransitionStatusChip";
import { formatTransitionDate } from "../utils";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import { Table } from "@src/sharedComponents/TableV2/Table";
import { TooltipWrapper } from "@src/sharedComponents/TooltipWrapper/TooltipWrapper";
import Text from "@src/sharedComponents/Typography/Text";

import { createTableColumns } from "./TransitionsTable.utils";

import styles from "./TransitionsTable.module.css";

const TABLE_COLUMN_WIDTHS = Array.from({ length: 7 }, () => "auto");
const PLACEHOLDER = "--";

export type TransitionTableColumns =
  | "team"
  | "advisors"
  | "launchDate"
  | "onboardingStatus"
  | "transitionStatus"
  | "sentiment"
  | "transitionedAum";

interface TransitionsTableProps {
  transitions: TransitionDTO[];
  pagination: PaginationMetadata | undefined;
  isLoading: boolean;
  isErrored: boolean;
  handleChangeQueryParams: (newQueryParams: GetTransitionsQueryParams) => void;
  onTransitionClick: (transitionId: string) => void;
}

const TransitionsTable: React.FC<TransitionsTableProps> = ({
  transitions,
  pagination,
  isLoading,
  isErrored,
  handleChangeQueryParams,
  onTransitionClick,
}) => {
  const queryClient = useQueryClient();
  const tableHeaders = useMemo(() => createTableColumns(), []);

  const handleOnPageChange = (page: number): void => {
    handleChangeQueryParams({ page });
  };

  const handleTransitionsListRefetch = (): void => {
    queryClient.invalidateQueries(transitionsKeyMaker(undefined));
  };

  const handleTransitionDetails = (transitionId: string): void => {
    onTransitionClick(transitionId);
  };

  return (
    <>
      <Table
        className={styles.table}
        resizable={false}
        initialColumnWidths={TABLE_COLUMN_WIDTHS}
      >
        <Table.Row className={styles.header}>
          {tableHeaders.map((header, index) => (
            <Table.Header
              key={header.accessor}
              columnIndex={index}
              sortable={header.sortable}
            >
              {header.label}
            </Table.Header>
          ))}
        </Table.Row>

        {isErrored && (
          <Table.Row className={styles.noTransitions}>
            <Table.Cell>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                style={{
                  justifyContent: "center",
                  marginBottom: "15px",
                }}
              >
                <ErrorIcon className={styles.icon} />
                <Text>Error loading transitions</Text>
              </Stack>
              <Button
                text="Refresh"
                size="small"
                variant="outlined"
                buttonType="primary"
                onClick={handleTransitionsListRefetch}
                endIcon={<RefreshIcon className={styles.icon} />}
              />
            </Table.Cell>
          </Table.Row>
        )}

        {!isErrored && !isLoading && transitions.length === 0 && (
          <Table.Row className={styles.noTransitions}>
            <Table.Cell>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                style={{
                  justifyContent: "center",
                }}
              >
                <ErrorIcon className={styles.icon} />
                <Text>No transitions found</Text>
              </Stack>
            </Table.Cell>
          </Table.Row>
        )}

        {isLoading && (
          <Fade in>
            <div className={styles.loadingContainer}>
              {Array.from({ length: 20 }).map((_, index) => (
                <Table.Row key={index}>
                  {Array.from({ length: tableHeaders.length }).map((_, idx) => (
                    <Table.Cell key={index + idx}>
                      <Skeleton width="100%" />
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </div>
          </Fade>
        )}

        {!isLoading &&
          transitions.map((transition) => {
            const teamValue = transition.team ?? PLACEHOLDER;
            // TODO: advisorValue will be updated once task returns multiple advisors
            const advisorsValue =
              transition.task.advisor && transition.task.advisor.name
                ? `${transition.task.advisor.name.first} ${transition.task.advisor.name.last}`
                : PLACEHOLDER;
            const dueDateValue = transition.task.dueDate
              ? formatTransitionDate(transition.task.dueDate)
              : PLACEHOLDER;
            const transitionedAumValue =
              transition.transitionedAum && transition.transitionedAumPercent
                ? `${transition.transitionedAumPercent}% (${formatShortCash(
                    transition.transitionedAum
                  )})`
                : PLACEHOLDER;

            return (
              <Table.Row
                key={transition.transitionId}
                onClick={() => handleTransitionDetails(transition.transitionId)}
              >
                <Table.Cell>
                  <TooltipWrapper tooltipText={teamValue} index={0}>
                    <Text variant="body" noWrap>
                      {teamValue}
                    </Text>
                  </TooltipWrapper>
                </Table.Cell>

                <Table.Cell>
                  <TooltipWrapper tooltipText={advisorsValue} index={1}>
                    <Text variant="body" noWrap>
                      {advisorsValue}
                    </Text>
                  </TooltipWrapper>
                </Table.Cell>

                <Table.Cell>
                  <TooltipWrapper tooltipText={dueDateValue} index={2}>
                    <Text variant="body">{dueDateValue}</Text>
                  </TooltipWrapper>
                </Table.Cell>

                <Table.Cell>
                  <TransitionStatusChip
                    status={transition.onboardingStatus}
                    variant="onboarding"
                  />
                </Table.Cell>

                <Table.Cell>
                  <TransitionStatusChip
                    status={transition.transitionStatus}
                    variant="transition"
                  />
                </Table.Cell>

                <Table.Cell>
                  {transition.sentiment ? (
                    <TransitionStatusChip
                      status={transition.sentiment}
                      variant="sentiment"
                    />
                  ) : (
                    <Text variant="body">{PLACEHOLDER}</Text>
                  )}
                </Table.Cell>

                <Table.Cell>
                  <TooltipWrapper tooltipText={transitionedAumValue} index={6}>
                    <Text variant="body">{transitionedAumValue}</Text>
                  </TooltipWrapper>
                </Table.Cell>
              </Table.Row>
            );
          })}
      </Table>

      {pagination && (
        <Table.Pagination
          pageSize={pagination.pageSize}
          currentPage={pagination.currentPage - 1}
          totalCount={pagination.totalCount}
          onPageChange={(page) => handleOnPageChange(page + 1)}
          isPrevButtonDisabled={pagination.currentPage === 1}
          isNextButtonDisabled={pagination.currentPage === pagination.pageCount}
        />
      )}
    </>
  );
};

export default TransitionsTable;
